import React, { useState, useEffect } from "react";
import { Loading, Text, Button as NextButton, Input, Radio } from '@nextui-org/react';
import { useSelector } from "react-redux";

import { mstore } from "../../store";
import endpoint from "../../config/endpoint.json";
import { secureFetch } from "../../lib/api";
import { error, success } from "../../lib/userAlerts";

// Component for detention window settings
const DetentionWindow = ({ value, onChange }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="flex items-center justify-between mb-4">
      <div className="text-lg font-medium text-gray-700">
        Detention Window
      </div>
      <div className="text-sm text-gray-500">
        Set the time window for detention calculation
      </div>
    </div>
    <div className="flex items-center space-x-4">
      <Input
        type="number"
        underlined
        css={{ width: '120px' }}
        min={1}
        max={23}
        defaultValue={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-lg"
      />
      <span className="text-gray-600">hours</span>
    </div>
  </div>
);

// Component for appointment time selection
const AppointmentTimeSelection = ({ value, onChange }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="flex items-center justify-between mb-4">
      <div className="text-lg font-medium text-gray-700">
        Appointment Time Selection
      </div>
      <div className="text-sm text-gray-500">
        Choose which appointment time to use for detention calculation
      </div>
    </div>
    <Radio.Group
      size="sm"
      row
      css={{ mt: '0.75rem', w: '100%', justifyContent: 'space-between' }}
      label="activate"
      value={value ? 1 : 0}
      onChange={(val) => onChange(val === 1)}
    >
      <Radio
        css={{ w: '48%' }}
        color="primary"
        value={1}
        className="p-4 border rounded-lg hover:bg-gray-50"
      >
        <div className="font-medium">Appointment Time 1</div>
        <Radio.Desc className="text-gray-600">Use Appointment Time-1 for calculating detention</Radio.Desc>
      </Radio>
      <Radio
        color="primary"
        css={{ w: '48%' }}
        value={0}
        className="p-4 border rounded-lg hover:bg-gray-50"
      >
        <div className="font-medium">Appointment Time 2</div>
        <Radio.Desc className="text-gray-600">Use Appointment Time-2 for calculating detention</Radio.Desc>
      </Radio>
    </Radio.Group>
  </div>
);

// Component for detention calculation method
const DetentionCalculationMethod = ({ value, onChange }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200">
    <div className="flex items-center justify-between mb-4">
      <div className="text-lg font-medium text-gray-700">
        Calculation Method
      </div>
      <div className="text-sm text-gray-500">
        Select the method for detention calculation
      </div>
    </div>
    <Radio.Group
      size="sm"
      row
      css={{ mt: '0.75rem', w: '100%', justifyContent: 'space-between' }}
      label="activate"
      value={value ? 1 : 0}
      onChange={(val) => onChange(val === 1)}
    >
      <Radio
        css={{ w: '48%' }}
        color="primary"
        value={1}
        className="p-4 border rounded-lg hover:bg-gray-50"
      >
        <div className="font-medium">Use Arrival Time</div>
        <Radio.Desc className="text-gray-600">Use Arrival Time for calculating detention</Radio.Desc>
      </Radio>
      <Radio
        color="primary"
        css={{ w: '48%' }}
        value={0}
        className="p-4 border rounded-lg hover:bg-gray-50"
      >
        <div className="font-medium">Use Check-In Time</div>
        <Radio.Desc className="text-gray-600">Use Check-In Time for calculating detention</Radio.Desc>
      </Radio>
    </Radio.Group>
  </div>
);

// Access denied component
const AccessDenied = () => (
  <div role="alert" className="max-w-2xl mx-auto mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg shadow-sm">
    <div className="flex items-center space-x-3">
      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <span className="text-yellow-800">Please subscribe to <b>Detention Management</b> feature</span>
    </div>
  </div>
);

const DetentionManagement = () => {
  // Redux state
  const access = useSelector(state => state.navigation.Access);
  const clientId = useSelector(state => 
    state.client?.id || mstore.getPath(["user", "company_id"])
  );

  // Local state
  const [settings, setSettings] = useState({
    detention_window_hrs: 4,
    use_appointment_time1: true,
    det_calc_using_arrival_time: true,
  });
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Fetch detention management settings
  const getDetentionManagementSettings = async () => {
    setLoading(true);
    
    try {
      const [ok, , json] = await secureFetch(endpoint.fetchDetentionManagementSettings, {});
      
      if (ok) {
        setSettings(json.detentionManagementSettings);
      } else {
        error("Oops! Unable to fetch the detention management settings.");
      }
    } catch (err) {
      error("Failed to fetch detention management settings");
    } finally {
      setLoading(false);
    }
  };

  // Update detention management settings
  const updateDetentionManagement = async () => {
    setUpdating(true);
    
    try {
      const [ok] = await secureFetch(endpoint.updateDetentionManagementSettings, {
        ...settings
      });
      
      if (ok) {
        success('Updated the detention management settings.');
      } else {
        error("Oops! Error occurred when updating your detention management settings.");
      }
    } catch (err) {
      error("Failed to update detention management settings");
    } finally {
      setUpdating(false);
    }
  };

  // Update individual settings
  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Initial data loading
  useEffect(() => {
    getDetentionManagementSettings();
  }, [clientId]);

  // Render loading state
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <Loading size="lg" className="text-blue-600 mb-4" />
            <Text className="text-gray-600">Fetching your detention parameters...</Text>
          </div>
        </div>
      </div>
    );
  }

  // Render access denied
  if (access.allow_detention_management !== 1) {
    return <AccessDenied />;
  }

  // Render detention parameters
  return (
    <div className="w-100 mt3">
      <div className="ml2 my-4 p-4 border-2 border-dotted rounded-xl shadow-lg">
        <div className="flex items-center justify-between border-b border-gray-100 px-4 pb-4">
          <h2 className="text-xl font-semibold text-gray-800">Detention Settings</h2>
          <div className="text-sm text-gray-500">
            Configure how detention is calculated and managed
          </div>
        </div>
   
        <div className="space-y-6">
          <DetentionWindow 
            value={settings.detention_window_hrs} 
            onChange={(value) => updateSetting('detention_window_hrs', value)} 
          />
          
          <AppointmentTimeSelection 
            value={settings.use_appointment_time1} 
            onChange={(value) => updateSetting('use_appointment_time1', value)} 
          />
          
          <DetentionCalculationMethod 
            value={settings.det_calc_using_arrival_time} 
            onChange={(value) => updateSetting('det_calc_using_arrival_time', value)} 
          />
        </div>

        <div className="flex justify-end pt-4">
            <NextButton
              auto
              color="primary"
              className="hover:bg-blue-600 transition-colors duration-200"
              disabled={updating}
              onClick={updateDetentionManagement}
            >
              {updating ? (
                <div className="flex items-center space-x-2">
                  <Loading size="sm" color="white" />
                  <Text color="white">Updating...</Text>
                </div>
              ) : (
                <Text color="white">Update Settings</Text>
              )}
            </NextButton>
          </div>
      </div>
    </div>
  );
};

export default DetentionManagement;
